/**
 * Filter drawer for the Customer Products view.
 * Uses the shared FilterDrawer for the outer drawer shell.
 * Currently only contains the Organizations filter (SUPER_ADMIN only).
 */
import { Stack } from "@mantine/core";
import { useForm } from "@mantine/form";
import { GraphQL } from "@rpi/openapi-api";
import { FilterDrawer, useFilterDrawerContext } from "../../components/filters/FilterDrawer";
import { SearchInput } from "../../configuration/filters/searchInput";
import {
  getCustomerPublicIdFilter,
  getInitialOrganization,
  NO_FILTER_VALUE,
  OrganizationFilter,
} from "../../components/filters/OrganizationFilter";
import { useFilterStyles } from "../../components/filters/filterStyles";
import { toSearchInputArray } from "../../configuration/filters/searchInput";

export const path = "filter";
const filterFormId = "customer-product-filter-form";

type CustomerProductFilterProps = {
  isOpened: boolean;
  filters: SearchInput<GraphQL.CustomerProductSearchField>[];
  onClose: () => void;
  onApply: (filter: SearchInput<GraphQL.CustomerProductSearchField>[]) => void;
};
export function CustomerProductFilter({ isOpened, filters, onClose, onApply }: CustomerProductFilterProps) {
  return (
    <FilterDrawer formId={filterFormId} isOpened={isOpened} filters={filters} onClose={onClose} onApply={onApply} />
  );
}

export function CustomerProductFilterInner() {
  const { classes } = useFilterStyles();
  const { initialFilters, onClose, onApply } = useFilterDrawerContext<GraphQL.CustomerProductSearchField>();

  const { getInputProps, onSubmit, setFieldValue } = useForm({
    initialValues: {
      selectedCustomer: getInitialOrganization(GraphQL.CustomerProductSearchField.CustomerPublicId, initialFilters),
    },
  });

  const handleApply = (customerPublicId: string) => {
    const filters = toSearchInputArray([
      getCustomerPublicIdFilter(GraphQL.CustomerProductSearchField.CustomerPublicId, customerPublicId),
    ]);
    onApply(filters);
    onClose();
  };

  const handleClear = () => {
    setFieldValue("selectedCustomer", NO_FILTER_VALUE);
  };

  return (
    <Stack className={classes.root}>
      <form
        id={filterFormId}
        className={classes.form}
        onSubmit={onSubmit((values) => handleApply(values.selectedCustomer))}
        onReset={handleClear}
      >
        <OrganizationFilter {...getInputProps("selectedCustomer")} />
      </form>
    </Stack>
  );
}
